import React, { ComponentProps } from 'react';
import { css } from '@/styled-system/css';
import { SystemStyleObject } from '@/styled-system/types';
import { Box, Text, ZillowLogo } from '@zillow/constellation-10';
import { BrandType, useBrandContext } from '~/core/components/BrandProvider';
import ShowingTimePlusLogo from '~/core/components/ShowingTimePlusLogo';

export const HEADER_HEIGHT = '4rem';

const MainHeader = ({
  showOnMobile,
  ...props
}: ComponentProps<'header'> & { showOnMobile: boolean }) => (
  <header
    className={css({
      display: showOnMobile ? 'flex' : 'none',
      alignItems: 'center',
      padding: '0 2rem',
      height: HEADER_HEIGHT,
      backgroundColor: '{colors.bg.neutral}',
      borderBottom: '1px solid {colors.border.muted}',
      zDocsLg: {
        display: 'flex',
      },
    })}
    {...props}
  />
);

const HeaderLeft = (props: ComponentProps<typeof Box>) => (
  <Box
    css={{
      flex: '0 0 40%',
      width: '40%',
    }}
    {...props}
  />
);

const HeaderCenter = (props: ComponentProps<typeof Box>) => (
  <Box
    css={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flex: '0 0 20%',
      width: '20%',
    }}
    {...props}
  />
);

const HeaderRight = (props: ComponentProps<typeof Box>) => (
  <Box
    css={{
      flex: '0 0 40%',
      width: '40%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    }}
    {...props}
  />
);

const Logo = ({ type, css }: { type: BrandType; css: SystemStyleObject }) => {
  if (type === BrandType.SHOWING_TIME_PLUS) {
    return <ShowingTimePlusLogo css={css} />;
  }

  return <ZillowLogo showLegacyLogo={type !== BrandType.ZILLOW} css={css} />;
};

interface Props {
  name?: string;
  showOnMobile?: boolean;
}

export function Header({ name, showOnMobile = false }: Props) {
  const brand = useBrandContext();

  return (
    <MainHeader showOnMobile={showOnMobile}>
      <HeaderLeft />
      <HeaderCenter>
        <Logo
          type={brand.type}
          css={{ height: `calc(${HEADER_HEIGHT} * 0.375)` }}
        />
      </HeaderCenter>
      <HeaderRight>
        <Text textStyle="body-sm-bold">{name}</Text>
      </HeaderRight>
    </MainHeader>
  );
}
